import { Bell, Download, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { isFinished, type JobDTO } from "../../api";
import { useAuth } from "../../auth";
import { Badge, Button, Dialog, cn, saveBlob } from "../../ui";
import { useJobCenter } from "./JobCenterProvider";

/**
 * The bell in the top bar. Carries a count of running work, so the state of a
 * long operation is visible from anywhere without opening anything.
 */
export function JobBell() {
  const { activeCount, setOpen, jobs } = useJobCenter();
  const { t } = useTranslation();

  const failed = jobs.filter((job) => job.status === "FAILED").length;

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      title={t("job.title", "Background jobs")}
      aria-label={t("job.title", "Background jobs")}
      className="relative inline-flex size-8 items-center justify-center rounded-md text-ink-faint hover:bg-surface-sunk hover:text-ink max-md:size-10"
    >
      <Bell className={cn("size-4", activeCount > 0 && "text-accent")} />
      {(activeCount > 0 || failed > 0) && (
        <span
          className={cn(
            "absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-4 text-ink-on-accent",
            activeCount > 0 ? "bg-accent" : "bg-risk",
          )}
        >
          {activeCount > 0 ? activeCount : failed}
        </span>
      )}
    </button>
  );
}

/** The panel itself: every recent job, its progress, and what to do about it. */
export function JobCenterDialog() {
  const { jobs, open, setOpen, client, refresh } = useJobCenter();
  const { t } = useTranslation();
  const [clearing, setClearing] = useState(false);

  // Only finished jobs can be cleared, so the button hides when there is
  // nothing it would actually remove.
  const finishedCount = jobs.filter((job) => isFinished(job.status)).length;

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      title={t("job.title", "Background jobs")}
      description={t("job.description", "Work running on the server. Safe to close, it keeps going.")}
      size="md"
      actions={
        <>
          {finishedCount > 0 && (
            <Button
              variant="ghost"
              className="mr-auto"
              icon={<Trash2 className="size-4" />}
              loading={clearing}
              onClick={async () => {
                setClearing(true);
                try {
                  await client.clearFinished();
                  refresh();
                } catch {
                  toast.error(t("job.clear.failed", "Could not clear the list"));
                } finally {
                  setClearing(false);
                }
              }}
            >
              {t("job.clear", "Clear all")}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t("button.close", "Close")}
          </Button>
        </>
      }
    >
      {jobs.length === 0 ? (
        <p className="py-8 text-center text-[13px] text-ink-muted">
          {t("job.none", "No recent jobs")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {jobs.map((job) => (
            <li key={job.id}>
              <JobRow
                job={job}
                onCancel={() => void cancel(job, client, refresh, t)}
                onDismiss={async () => {
                  try {
                    await client.dismiss(job.id);
                    refresh();
                  } catch {
                    toast.error(t("job.clear.failed", "Could not clear the list"));
                  }
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

async function cancel(
  job: JobDTO,
  client: ReturnType<typeof useJobCenter>["client"],
  refresh: () => void,
  t: (key: string, fallback: string) => string,
) {
  try {
    await client.cancel(job.id);
    refresh();
  } catch {
    toast.error(t("job.cancel.failed", "Could not cancel that job"));
  }
}

function JobRow({
  job,
  onCancel,
  onDismiss,
}: {
  job: JobDTO;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const running = !isFinished(job.status);

  const variant =
    job.status === "SUCCEEDED"
      ? "success"
      : job.status === "FAILED"
        ? "error"
        : job.status === "CANCELLED"
          ? "neutral"
          : "accent";

  const download = async () => {
    if (!job.download_path) return;
    setDownloading(true);
    try {
      const { blob, filename } = await http.download(job.download_path, { method: "GET" });
      saveBlob(blob, filename);
    } catch {
      toast.error(t("job.download.failed", "Download failed"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[13px] font-medium text-ink">{job.label}</span>
            <Badge variant={variant} size="sm">
              {job.status}
            </Badge>
          </div>

          <p className="mt-0.5 truncate text-[12px] text-ink-muted">
            {job.error ?? job.message ?? ""}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {job.status === "SUCCEEDED" && job.download_path && (
            <Button
              size="sm"
              variant="secondary"
              loading={downloading}
              onClick={() => void download()}
              icon={<Download className="size-3.5" />}
            >
              {t("button.download", "Download")}
            </Button>
          )}
          {/* The same corner button means "stop this" while it runs and
              "dismiss this" once it can't. */}
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={running ? t("job.cancel", "Cancel") : t("job.dismiss", "Dismiss")}
            aria-label={running ? t("job.cancel", "Cancel") : t("job.dismiss", "Dismiss")}
            onClick={running ? onCancel : onDismiss}
            icon={<X className="size-3.5" />}
          />
        </div>
      </div>

      {running && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-sunk">
          {/* A job that can't report a fraction gets an indeterminate bar
              rather than a fake percentage. */}
          <div
            className={cn(
              "h-full rounded-full bg-accent",
              job.progress < 0 && "w-1/3 animate-pulse",
            )}
            style={job.progress >= 0 ? { width: `${job.progress}%` } : undefined}
          />
        </div>
      )}

      {job.details.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="text-[12px] text-accent hover:underline"
          >
            {expanded
              ? t("job.details.hide", "Hide details")
              : t("job.details.show", "{{count}} details", { count: job.details.length })}
          </button>
          {expanded && (
            <ul className="mt-1 max-h-40 overflow-auto rounded border border-line bg-surface-sunk p-2">
              {job.details.map((detail, index) => (
                <li key={index} className="text-[12px] text-ink-muted">
                  {detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
